import { useRef, useState } from "react";
import { FieldNameSchema, type ProfileField } from "@starsach/shared-types";
import { useVoices } from "../../../api/hooks";
import { Speak } from "../../../components/AudioPreview";
import { Hint } from "../../../components/Hint";
import { Card, DataTable, Dialog, EmptyState, StatusPill, btnPrimary, btnSecondary, inputClass } from "../../../components/ui";
import { SSML_TAGS, fromSsml, isSsml, toSsml } from "../../../lib/ssml";
import { useUiStore } from "../../../store/ui";
import { NewOverride, OverrideRow } from "../parts";
import { describeOverride } from "../EmergencyClose";
import { TabMenu, type CsvShape } from "./TabMenu";

type Announcement = Extract<ProfileField, { type: "announcement" }>;

const shape: CsvShape<Announcement> = {
  name: "announcements",
  headers: ["name", "payload"],
  example: [
    { type: "announcement", name: "welcome", value: "Thank you for calling ACME." },
    { type: "announcement", name: "closed_christmas", value: '<speak>We are closed for Christmas. <break time="500ms"/> Please call back on the twenty-seventh.</speak>' },
  ],
  toRow: (a) => ({ name: a.name, payload: a.value }),
  fromRow: (r, line) => {
    const name = (r["name"] ?? "").trim();
    if (!FieldNameSchema.safeParse(name).success) return `Line ${line}: "${name}" is not a name a flow can read - lower-case letters, digits and underscores.`;
    if (!r["payload"]?.trim()) return `Line ${line}: ${name} has no payload.`;
    return { type: "announcement", name, value: r["payload"]!.trim() };
  },
};

/**
 * Announcements (the owner's ask of 2026-09-18): a name the flow reads it
 * by and a payload the caller hears - text, or SSML with a toggle that
 * wraps it in <speak> and a quick-select of the tags Connect understands
 * - heard before it is published with play, pause and stop, in a voice
 * chosen from Polly's own list for the region (what Connect offers too).
 * Holidays and "say when closed" refer to these by name.
 */
export function AnnouncementsTab({
  environmentId,
  profileId,
  fields,
  draft,
  overrides,
  onUpdate,
  onAdd,
  onRemove,
  onReplaceAll,
  can = { create: true, delete: true },
}: {
  environmentId: string;
  profileId: string;
  fields: Announcement[];
  draft: ProfileField[];
  overrides: Array<{ field: string; value: unknown; expiresAt?: string; setBy: string; reason: string }>;
  onUpdate: (field: ProfileField, next: ProfileField) => void;
  onAdd: (field: ProfileField) => void;
  onRemove: (field: ProfileField) => void;
  onReplaceAll: (fields: Announcement[]) => void;
  /** ADR-0017: the create and delete verbs on announcements; edit is the tab's own gate. */
  can?: { create: boolean; delete: boolean };
}) {
  const [editing, setEditing] = useState<Announcement | "new">();
  const voice = useUiStore((s) => s.previewVoice);
  const setVoice = useUiStore((s) => s.setPreviewVoice);
  return (
    <Card title="Announcements">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button type="button" className={btnPrimary} disabled={!can.create} onClick={() => setEditing("new")}>New announcement</button>
        <span className="flex items-center gap-2 text-sm">
          <span className="text-neutral-500" aria-hidden="true">Voice</span>
          <VoiceSelect environmentId={environmentId} value={voice} onChange={setVoice} label="Preview voice" />
          <Hint id="flow.voice" label="about the voices" />
        </span>
        <span className="ml-auto">
          <TabMenu
            what="Announcements"
            shape={shape}
            items={fields}
            canImport={can.create}
            canDeleteAll={can.delete}
            onImport={(items, replace) => {
              const byName = new Map((replace ? [] : fields).map((a) => [a.name, a]));
              for (const a of items) byName.set(a.name, a);
              onReplaceAll([...byName.values()]);
            }}
            onDeleteAll={() => onReplaceAll([])}
          />
        </span>
      </div>
      <DataTable
        caption="Announcements in this profile"
        rows={fields}
        rowKey={(a) => a.name}
        sortBy={(a) => a.name}
        filter={fields.length > 5}
        empty={<EmptyState title="No announcements yet" hint="An announcement has a name the flow reads it by and a payload the caller hears. Add one, or import a CSV - the example shows the shape." />}
        columns={[
          { key: "name", header: "Name", render: (a) => <code className="text-xs">{a.name}</code> },
          {
            key: "payload",
            header: "What the caller hears",
            render: (a) => (
              <span className="block max-w-xl">
                <span className="line-clamp-2 text-sm">{isSsml(a.value) ? fromSsml(a.value) : a.value}</span>
                {isSsml(a.value) && <StatusPill status="SSML" tone="neutral" />}
              </span>
            ),
          },
          { key: "listen", header: "", render: (a) => <Speak environmentId={environmentId} text={a.value} ssml={isSsml(a.value)} voice={voice} label={a.name} /> },
          {
            key: "edit",
            header: "",
            render: (a) => (
              <span className="flex gap-3 text-xs">
                <button type="button" className="underline" onClick={() => setEditing(a)}>Edit</button>
                {can.delete && <button type="button" className="text-red-700 underline dark:text-red-300" onClick={() => onRemove(a)} aria-label={`Remove ${a.name}`}>Remove</button>}
              </span>
            ),
          },
        ]}
      />
      {fields.map((a) => {
        const key = `announcement/${a.name}`;
        const override = overrides.find((o) => o.field === key);
        return override ? <OverrideRow key={key} environmentId={environmentId} profileId={profileId} field={key} override={override} describe={describeOverride} /> : null;
      })}
      {fields.length > 0 && (
        <div className="mt-4 flex items-center gap-2">
          <NewOverride environmentId={environmentId} profileId={profileId} fields={fields} />
          <Hint id="flow.override" label="what a temporary replacement does" />
        </div>
      )}
      {editing && (
        <AnnouncementDialog
          environmentId={environmentId}
          existing={draft}
          announcement={editing === "new" ? undefined : editing}
          voice={voice}
          onVoice={setVoice}
          onClose={() => setEditing(undefined)}
          onSave={(next) => {
            if (editing === "new") onAdd(next);
            else onUpdate(editing, next);
            setEditing(undefined);
          }}
        />
      )}
    </Card>
  );
}

/** Polly's voices for the region, by language - the list Connect's Play prompt block offers; the preview voice is a preference of the ear, kept per browser. */
export function VoiceSelect({ environmentId, value, onChange, label }: { environmentId: string; value: string; onChange: (voice: string) => void; label: string }) {
  const voices = useVoices(environmentId);
  const list = voices.data?.voices ?? [];
  const languages = [...new Set(list.map((v) => v.language))];
  const known = list.some((v) => v.id === value);
  return (
    <select className={`${inputClass} mt-0 w-auto`} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)} disabled={voices.isPending}>
      {(!known || list.length === 0) && <option value={value}>{voices.isPending ? "Loading voices…" : voices.isError ? "Voices unavailable" : value}</option>}
      {languages.map((lang) => (
        <optgroup key={lang} label={lang}>
          {list.filter((v) => v.language === lang).map((v) => <option key={v.id} value={v.id}>{v.name} ({v.gender}{v.engines.includes("neural") ? "" : ", standard"})</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function AnnouncementDialog({ environmentId, existing, announcement, voice, onVoice, onClose, onSave }: { environmentId: string; existing: ProfileField[]; announcement: Announcement | undefined; voice: string; onVoice: (voice: string) => void; onClose: () => void; onSave: (next: Announcement) => void }) {
  const [name, setName] = useState(announcement?.name ?? "");
  const [ssml, setSsml] = useState(announcement ? isSsml(announcement.value) : false);
  const [text, setText] = useState(announcement ? (isSsml(announcement.value) ? fromSsml(announcement.value) : announcement.value) : "");
  const [problem, setProblem] = useState<string>();
  const area = useRef<HTMLTextAreaElement>(null);
  const payload = ssml ? toSsml(text) : text;

  const insert = (snippet: string) => {
    const el = area.current;
    if (!el) return setText((t) => t + snippet);
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? start;
    const next = text.slice(0, start) + snippet + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  };

  return (
    <Dialog title={announcement ? `Edit ${announcement.name}` : "New announcement"} onClose={onClose} wide>
      <form
        className="grid gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          const n = name.trim();
          if (!FieldNameSchema.safeParse(n).success) return setProblem("A name is lower-case letters, digits and underscores, starting with a letter or digit: welcome, hold_1, closed_christmas. It is what the flow reads the announcement by.");
          if (!announcement && existing.some((f) => f.type === "announcement" && f.name === n)) return setProblem(`There is already an announcement called ${n}.`);
          if (!text.trim()) return setProblem("The payload is what the caller hears - it cannot be empty.");
          onSave({ type: "announcement", name: n, value: payload });
        }}
      >
        <label className="text-sm">
          <span className="block font-medium">Name</span>
          <input className={inputClass} value={name} disabled={Boolean(announcement)} placeholder="closed_christmas" aria-label="Announcement name" onChange={(e) => { setName(e.target.value); setProblem(undefined); }} />
          <span className="mt-1 block text-xs text-neutral-500">What the flow reads it by: <code>$.External.announcements.{name || "name"}</code>.</span>
        </label>
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
          <label className="text-sm">
            <span className="flex items-center justify-between font-medium">
              Payload - what the caller hears
              <span className="flex items-center gap-1 text-xs font-normal">
                <input type="checkbox" checked={ssml} onChange={(e) => setSsml(e.target.checked)} aria-label="SSML" />
                SSML
              </span>
            </span>
            <textarea ref={area} className={`${inputClass} min-h-32 font-mono text-xs`} value={text} aria-label="Announcement payload" onChange={(e) => { setText(e.target.value); setProblem(undefined); }} maxLength={4000} />
            {ssml && <span className="mt-1 block text-xs text-neutral-500">Sent as <code>&lt;speak&gt;…&lt;/speak&gt;</code>; the flow's Play prompt block must be set to interpret SSML.</span>}
          </label>
          {ssml && (
            <div className="rounded-lg border border-neutral-200 p-2 text-xs dark:border-neutral-800" role="group" aria-label="SSML tags Connect supports">
              <p className="mb-1 font-medium">Insert a tag</p>
              <ul className="grid max-h-48 gap-1 overflow-y-auto">
                {SSML_TAGS.map((t) => (
                  <li key={t.tag}>
                    <button type="button" className="w-full rounded px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800" title={t.hint} onClick={() => insert(t.snippet)}>
                      <span className="font-medium">{t.label}</span> <code className="text-[10px] text-neutral-500">&lt;{t.tag}&gt;</code>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3" role="group" aria-label="Hear it">
          <span className="flex items-center gap-2 text-sm">
            <span className="text-neutral-500" aria-hidden="true">Voice</span>
            <VoiceSelect environmentId={environmentId} value={voice} onChange={onVoice} label="Voice to hear it in" />
          </span>
          <Speak environmentId={environmentId} text={payload} ssml={ssml} voice={voice} label={name || "this announcement"} />
        </div>
        {problem && <p role="alert" className="text-sm text-red-700 dark:text-red-300">{problem}</p>}
        <div className="flex gap-2">
          <button type="submit" className={btnPrimary}>{announcement ? "Save" : "Add"}</button>
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
